import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Breadcrumbs, Button, Card, Collapse, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, IconButton, Link as MLink, MenuItem, Select, Stack, TextField, Tooltip, Typography,
  useMediaQuery,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RateReviewIcon from "@mui/icons-material/RateReview";
import EventNoteIcon from "@mui/icons-material/EventNote";
import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppDatePicker } from "../../../Components/DatePicker";
import PremiumTimePicker from "../../../Components/PremiumTimePicker";
import {
  useSessionAutosaveMutation, useSessionAddEntryMutation, useSessionUndoEntryMutation,
  useSessionDeleteEntryMutation, useSessionCompleteMutation, useSessionSubmitReviewMutation,
  useLazyGetSessionByIdQuery, useLazyGetSessionDraftCurrentQuery,
  useLazySearchSessionStudentsQuery, useLazySearchSessionStaffQuery,
  useLazySearchSessionProgramsQuery, useLazySearchSessionTargetsQuery,
  useGetPromptTemplateQuery, useLazyListProgramAssignmentsQuery,
  useLazySearchAbaStaffQuery,
} from "../../../Api/attoDeskApi";
import { Chip } from "@mui/material";
import { useNotifier } from "../../../Core/Notifier";
import AsyncPicker from "../shared/AsyncPicker";
import AutosaveIndicator, { AutosaveState } from "../shared/AutosaveIndicator";
import AssignAbaProgramDrawer from "../shared/AssignAbaProgramDrawer";
import { ABA } from "../../ProgramLibrary/shared/abaDesign";
import { PageSurface, LegacyTokens, TypeScale, B13, B13Ink, tint12 } from "../../../theme/b13Tokens";
import { StudentAvatar } from "../shared/SessionBadges";
import SessionEntryLog from "../shared/SessionEntryLog";
import {
  PromptLevelSelector, TrialBasedRecorder, TaskAnalysisRecorder, FrequencyRecorder,
  DurationRecorder, LatencyRecorder, IntervalRecorder, AbcBehaviourRecorder,
} from "../shared/Recorders";
import { toMeasurementTypeEnum } from "../shared/measurementType";
import { measurementTypeLabel } from "../shared/measurementTypeOptions";
import { filterEligibleForRecorder, classifyBlockingMessage } from "../shared/staffEligibility";

/**
 * Batch 35 — Session Recorder.
 *
 * Backing store is session_data_records (draft) + session_data_entries (one
 * row per interaction). Autosave is debounced (1s) and idempotent by the
 * per-user draftKey. Refresh recovery routes here from the index via
 * /aba-therapy/sessions/:id/edit.
 */
export default function SessionRecorder() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { id: routeId } = useParams<{ id: string }>();
  const { showMessage, showErrorMessage } = useNotifier();

  const [sessionId, setSessionId] = useState<number | null>(routeId ? Number(routeId) : null);
  const [autosaveState, setAutosaveState] = useState<AutosaveState>("idle");
  const [savedAt, setSavedAt] = useState<number | undefined>();
  const [autosaveVersion, setAutosaveVersion] = useState(0);

  // Context
  const [student, setStudent] = useState<any>(null);
  const [program, setProgram] = useState<any>(null);
  const [target, setTarget] = useState<any>(null);
  const [staff, setStaff] = useState<any>(null);
  // Batch 51 §4b — use LOCAL date. `toISOString` returns UTC, which
  // can bump one day forward for timezones east of UTC around midnight.
  // The previous default sometimes rendered a date one or more days
  // ahead of the operator's clock.
  const [sessionDate, setSessionDate] = useState<string>(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  });
  // Batch 53 §4 — capture Start Time on page mount. HH:MM local. End
  // Time is left empty and stamped from the client clock on Complete
  // Session (if still blank) so a technician who never touches the
  // clock still gets a proper duration. Backend already has startedAt
  // and completedAt timestamp columns — no migration.
  const [startTime, setStartTime] = useState<string>(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
  const [endTime, setEndTime] = useState<string>("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [measurementType, setMeasurementType] = useState<string>("Percent Correct");
  const [readOnly, setReadOnly] = useState(false);
  const [status, setStatus] = useState<string>("draft");

  const [entries, setEntries] = useState<any[]>([]);
  const [prompt, setPrompt] = useState<any>(null);
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverCandidate, setRecoverCandidate] = useState<any>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const startTimeRef = useRef<number>(Date.now());

  // Batch 49 §2.2 — More Options disclosure (Session Date + Measurement
  // + Location + Notes). Defaults are sensible; a technician mid-session
  // should not have to think about them.
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
  // Editing the context strip in Phase 2 opens a compact edit dialog
  // rather than re-showing the whole setup card, per §2.2.
  const [contextEditOpen, setContextEditOpen] = useState(false);
  // Batch 49 §7 — honour prefers-reduced-motion for the accuracy ring.
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  // Hooks
  const [autosave] = useSessionAutosaveMutation();
  const [addEntry] = useSessionAddEntryMutation();
  const [undoEntry] = useSessionUndoEntryMutation();
  const [deleteEntry] = useSessionDeleteEntryMutation();
  const [completeSession] = useSessionCompleteMutation();
  const [submitReview] = useSessionSubmitReviewMutation();
  const [triggerFetch] = useLazyGetSessionByIdQuery();
  const [triggerDraft] = useLazyGetSessionDraftCurrentQuery();
  const [searchStudents] = useLazySearchSessionStudentsQuery();
  const [searchStaff] = useLazySearchSessionStaffQuery();
  const [searchPrograms] = useLazySearchSessionProgramsQuery();
  const [searchTargets] = useLazySearchSessionTargetsQuery();
  const [fetchAssignmentsForProgram] = useLazyListProgramAssignmentsQuery();
  const [searchAbaStaff] = useLazySearchAbaStaffQuery();
  const [studentGroup, setStudentGroup] = useState<string>("my_aba_students");

  // Prompt levels — Batch 34 §2.2: prefer the target's `effectivePromptTemplateId`
  // (server-resolved: target override wins over program) and fall back to the
  // raw target field for older recorder payloads that don't carry the effective
  // one yet.
  const effectivePromptTemplateId = target?.effectivePromptTemplateId ?? target?.promptTemplateId ?? null;
  // Batch 36B — surface the fetch's isFetching so PromptLevelSelector can
  // render a placeholder instead of the "No prompt hierarchy" warning
  // while the request is in flight.
  const { data: promptTemplateData, isFetching: promptTemplateLoading } =
    useGetPromptTemplateQuery(effectivePromptTemplateId ? { id: effectivePromptTemplateId } : {}, { skip: !effectivePromptTemplateId });
  const promptLevels: any[] = useMemo(() => {
    const rows: any[] = Array.isArray(promptTemplateData?.data) ? (promptTemplateData!.data as any[]) : [];
    const match = effectivePromptTemplateId ? rows.find((r) => String(r.id) === String(effectivePromptTemplateId)) : rows[0];
    return match?.levels?.map((l: any) => ({ id: l.id, levelName: l.levelName, colour: l.colour })) ?? [];
  }, [promptTemplateData, effectivePromptTemplateId]);

  // Load existing session if edit route
  useEffect(() => {
    if (!routeId) return;
    (async () => {
      const r: any = await triggerFetch(routeId).unwrap();
      if (r?.status && r.data) {
        const s = r.data.session;
        setSessionId(Number(s.id));
        setStatus(s.status || "draft");
        setSessionDate(s.sessionDate || sessionDate);
        setMeasurementType(s.measurementType || "Percent Correct");
        setLocation(s.locationName || "");
        setNotes(s.notes || "");
        // Batch 53 §4 — hydrate Start/End Time from stored timestamps
        // when this is an edit-mode load. Format "HH:MM" from
        // startedAt/completedAt. If the record has no times (e.g. a
        // pre-Batch-53 session), leave the fields empty rather than
        // faking a value — the FE renders "—" for missing times.
        const timeOnly = (ts: string | null | undefined): string => {
          if (!ts) return "";
          const d = new Date(ts);
          if (Number.isNaN(d.getTime())) return "";
          return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
        };
        setStartTime(timeOnly(s.startedAt));
        setEndTime(timeOnly(s.completedAt));
        setAutosaveVersion(Number(s.autosaveVersion || 0));
        if (s.studentId) setStudent({ id: s.studentId, label: s.studentName });
        if (s.programId) setProgram({ id: s.programId, label: s.programName });
        if (s.targetId)  setTarget({ id: s.targetId, label: s.targetName });
        if (s.staffId)   setStaff({ id: s.staffId, label: s.staffName });
        setEntries(r.data.entries || []);
        if (!["draft", "returned_for_correction", "completed"].includes(s.status)) setReadOnly(true);
      }
    })();
  }, [routeId]);

  // Batch 51 §1 — preselect student/program/staff from URL query params
  // when the recorder is opened fresh from Today's Sessions or the
  // Assign flow. The recorder used to destructure `useSearchParams`
  // and never read it, so clicking Start Session on a card had no
  // effect on the recorder's initial state. That let the URL and the
  // display disagree, which for a clinical record is unacceptable —
  // fixed here by making the URL the single source of truth for the
  // opening context. The pickers still allow manual override.
  useEffect(() => {
    if (routeId) return; // edit route loads from session record instead
    const sid = search.get("studentId");
    const pid = search.get("programId");
    const stf = search.get("staffId");
    if (!sid && !pid && !stf) return;
    (async () => {
      if (sid && Number(sid) > 0) {
        try {
          const r: any = await searchStudents({ groupType: "all_aba", limit: 25 } as any);
          const rows: any[] = r?.data?.data ?? [];
          const match = rows.find((s: any) => String(s.id) === String(sid));
          if (match) setStudent({ id: match.id, label: `${match.first_name ?? ""} ${match.last_name ?? ""}`.trim(), ...match });
        } catch {}
      }
      if (pid && Number(pid) > 0 && sid) {
        try {
          const r: any = await searchPrograms({ studentId: sid } as any);
          const rows: any[] = r?.data?.data ?? [];
          const match = rows.find((p: any) => String(p.id) === String(pid));
          if (match) setProgram({ id: match.id, label: match.label });
        } catch {}
      }
      if (stf && Number(stf) > 0 && sid) {
        try {
          const r: any = await searchAbaStaff({ student_id: sid, ...(pid ? { program_id: pid } : {}), group_type: "location", limit: 20 } as any);
          // Same eligibility gate the picker applies — a URL-param
          // preselect must not slip a location/PSA-mismatched staff
          // past the picker's filter and into the recorder's state.
          const rows: any[] = filterEligibleForRecorder(r?.data?.data ?? []);
          const match = rows.find((s: any) => String(s.id) === String(stf));
          if (match) setStaff({ id: match.id, label: match.label });
        } catch {}
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, search]);

  // Draft recovery check when student+target chosen fresh
  useEffect(() => {
    if (routeId || !student?.id || !target?.id) return;
    triggerDraft({ studentId: student.id, targetId: target.id }).then((r: any) => {
      const d = r?.data?.data;
      if (d && d.id && d.id !== sessionId) { setRecoverCandidate(d); setRecoverOpen(true); }
    });
  }, [student?.id, target?.id, routeId]);

  // Bind measurement type from target — Batch 34 §2.2 prefers the
  // server-computed effective value (target ?? program). Batch 15
  // Step 2 — prefer the enum field (`effectiveMeasurementTypeEnum`)
  // when the endpoint has been extended to return it; fall back to
  // the legacy varchar for anything the enum-column resolution
  // couldn't populate. `toMeasurementTypeEnum` in the dispatch
  // handles either shape.
  useEffect(() => {
    const enumMt = (target as any)?.effectiveMeasurementTypeEnum ?? (target as any)?.measurementTypeEnum;
    const legacyMt = target?.effectiveMeasurementType ?? target?.measurementType;
    const mt = enumMt ?? legacyMt;
    if (mt) setMeasurementType(mt);
  }, [
    (target as any)?.effectiveMeasurementTypeEnum,
    (target as any)?.measurementTypeEnum,
    target?.effectiveMeasurementType,
    target?.measurementType,
  ]);

  // Auto-fill Staff using the ABA-aware staff endpoint. Runs whenever
  // Student or Program changes. Only auto-fills when exactly one usable
  // Staff member is assigned to this Student+Program. Otherwise leaves the
  // picker open for the user and shows a warning if the assigned staff is
  // unavailable.
  const [autoFillWarning, setAutoFillWarning] = useState<string | null>(null);
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
  useEffect(() => {
    setAutoFillWarning(null);
    setStaff(null);
    if (!student?.id) return;
    // Only auto-fill when a Program is chosen — auto-fill needs both anchors
    // (student + program). Without a Program we still need to leave the
    // picker empty so the user can browse eligible ABA Staff.
    if (!program?.id) return;
    const params: any = { q: "", student_id: student.id, program_id: program.id, group_type: "location", limit: 15 };
    searchAbaStaff(params).then((r: any) => {
      // Filter through the same recorder-eligibility gate the picker
      // uses so single-match auto-select can't fill an invalid staff
      // (was the third bypass path — the assign endpoint's `usable`
      // flag doesn't cover location or PSA mismatch).
      const rows: any[] = filterEligibleForRecorder(
        Array.isArray(r?.data?.data) ? r.data.data : []
      );
      const usableAssigned = rows.filter((s) => s.assigned_to_student_program && s.usable);
      if (usableAssigned.length === 1) {
        setStaff({ id: usableAssigned[0].id, label: usableAssigned[0].label });
        return;
      }
      const anyAssigned = rows.filter((s) => s.assigned_to_student_program || s.assigned_to_student);
      if (anyAssigned.length > 0 && usableAssigned.length === 0) {
        setAutoFillWarning("No active ABA Staff is currently assigned to this Student and Program. Select an authorized Staff member.");
      }
    });
  }, [program?.id, student?.id]);

  // Debounced autosave for context changes with exponential backoff on
  // network / retryable-5xx failures. Validation-style failures (4xx / status:
  // false with a message) stop retries and require an explicit Retry Now.
  const [autosaveFailures, setAutosaveFailures] = useState(0);
  const [autosaveBackoffMs, setAutosaveBackoffMs] = useState(1000);
  const [autosaveBlockingMessage, setAutosaveBlockingMessage] = useState<string | null>(null);
  const [manualRetryToken, setManualRetryToken] = useState(0);
  const canAutosave = !!(student?.id && target?.id && staff?.id) || !!sessionId;
  useEffect(() => {
    if (readOnly) return;
    if (!canAutosave) {
      setAutosaveState("idle"); setAutosaveBlockingMessage(null);
      setAutosaveFailures(0); setAutosaveBackoffMs(1000);
      return;
    }
    if (autosaveBlockingMessage) { setAutosaveState("error"); return; }
    if (autosaveFailures >= 3) { setAutosaveState("error"); return; }
    const delay = autosaveFailures === 0 ? 1000 : autosaveBackoffMs;
    const t = setTimeout(async () => {
      setAutosaveState("saving");
      try {
        // Batch 53 §4 — send startedAt/completedAt as "YYYY-MM-DD HH:MM:00"
        // when the corresponding time field is populated. The backend's
        // startedAt/completedAt columns are timestamp NULL and accept
        // this shape directly.
        const composeTs = (t: string): string | null =>
          t && sessionDate ? `${sessionDate} ${t}:00` : null;
        const body: any = {
          id: sessionId ?? undefined,
          studentId: student?.id ?? null, programId: program?.id ?? null,
          targetId: target?.id ?? null, staffId: staff?.id ?? null,
          sessionDate, measurementType, locationName: location, notes,
          startedAt: composeTs(startTime),
          completedAt: composeTs(endTime),
          promptTemplateIdSnapshot: target?.promptTemplateId ?? null,
          autosaveVersion,
        };
        const r: any = await autosave(body).unwrap();
        if (r?.status) {
          if (r.data?.sessionId && !sessionId) setSessionId(Number(r.data.sessionId));
          if (r.data?.version != null) setAutosaveVersion(Number(r.data.version));
          setAutosaveState("saved"); setSavedAt(Date.now());
          setAutosaveFailures(0); setAutosaveBackoffMs(1000);
        } else {
          // status:false is a validation-style rejection — do NOT retry.
          setAutosaveBlockingMessage(r?.message || "Complete the required Session details before saving.");
          setAutosaveState("error");
        }
      } catch (e: any) {
        // Only retry on network / server errors (no `data`/`data.status` present).
        setAutosaveFailures((n) => n + 1);
        setAutosaveBackoffMs((b) => Math.min(b * 2.5, 10000));
        setAutosaveState("error");
      }
    }, delay);
    return () => clearTimeout(t);
  }, [student?.id, program?.id, target?.id, staff?.id, sessionDate, location, notes, measurementType, readOnly, canAutosave, autosaveFailures, autosaveBlockingMessage, manualRetryToken]);

  // Warn on unload with dirty entries
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (autosaveState === "saving" || autosaveState === "error") { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [autosaveState]);

  const requireSession = async (): Promise<number | null> => {
    if (sessionId) return sessionId;
    // Force an autosave first so we have an id.
    setAutosaveState("saving");
    const composeTs2 = (t: string): string | null =>
      t && sessionDate ? `${sessionDate} ${t}:00` : null;
    const body: any = {
      studentId: student?.id ?? null, programId: program?.id ?? null,
      targetId: target?.id ?? null, staffId: staff?.id ?? null,
      sessionDate, measurementType, locationName: location, notes,
      startedAt: composeTs2(startTime),
      completedAt: composeTs2(endTime),
      promptTemplateIdSnapshot: target?.promptTemplateId ?? null,
    };
    const r: any = await autosave(body).unwrap();
    if (r?.status && r.data?.sessionId) {
      setSessionId(Number(r.data.sessionId));
      setAutosaveVersion(Number(r.data.version || 1));
      setAutosaveState("saved"); setSavedAt(Date.now());
      return Number(r.data.sessionId);
    }
    setAutosaveState("error");
    return null;
  };

  const contextValid = student && program && target && staff && sessionDate;
  // Batch 49 §2.2 / Batch 51 §2 — three phases now: setup, record, and
  // Phase — SETUP (wizard) or RECORD (trials surface). "blocked" was a
  // third phase that took over the whole page whenever autosave rejected
  // a field-level combination; that reads as "the app broke" for a
  // failure that's about one field. Now: when the server rejects, we
  // classify the message, force the user back to the wizard at that
  // field, and surface the message inline. Wizard stays intact, other
  // selections preserved.
  const rawPhase: "setup" | "record" =
    !contextValid ? "setup" : "record";

  // Classify the autosave rejection to the wizard step whose field
  // owns it. Logic lives in shared/staffEligibility.ts alongside the
  // filter used by the picker + auto-fill sites; tested by
  // staffEligibility.test.ts (which locks in the current server strings
  // as assertions so a message rewrite trips a failure).
  const blockingStep: number | null = React.useMemo(
    () => classifyBlockingMessage(autosaveBlockingMessage),
    [autosaveBlockingMessage]
  );

  // Effective render decisions — the rejection forces the user back to
  // setup at the offending step. State is preserved; the picker at that
  // step just shows an inline error above it.
  const phase: "setup" | "record" = blockingStep !== null ? "setup" : rawPhase;
  const effectiveStepNumber = blockingStep ?? undefined; // undefined = let derivation win

  // Batch 49 §4 — keyboard bindings for the recording surface. Only
  // active in Phase 2 (recording), only in Trial Accuracy mode, and
  // only when focus is NOT inside a text input.
  //   Z         → Incorrect
  //   X         → Correct
  //   1..9      → select the Nth active prompt level
  //   Backspace → undo the last entry
  // Z and X sit adjacent bottom-left, reach with one hand while the
  // other is with the child; symmetric weight for two equal actions.
  // Neither shadows any global shortcut in this app or the browser
  // (Ctrl/Cmd modifiers are required for the standard cut/undo).
  // Modifier keys are excluded on every branch so Ctrl+Z / Cmd+X etc.
  // continue to work as the browser expects them to.
  useEffect(() => {
    if (phase !== "record" || readOnly) return;
    const isTypingTarget = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName.toLowerCase();
      return tag === "input" || tag === "textarea" || el.isContentEditable;
    };
    const h = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      // Batch 15 Step 2 — number-key shortcuts (1..9 selects a prompt
      // level) apply ONLY to the trial-based recorder. Derived from
      // the enum, not from substring matching on a free-text label,
      // so a mistyped or unknown measurement type disables shortcuts
      // rather than enabling them by accident.
      const isTrial = toMeasurementTypeEnum(measurementType) === "trial_percent_correct";
      if (!isTrial) return;
      const k = e.key.toLowerCase();
      if (/^[1-9]$/.test(e.key)) {
        const idx = parseInt(e.key, 10) - 1;
        const lvl = promptLevels[idx];
        if (lvl) { setPrompt(lvl); e.preventDefault(); }
        return;
      }
      if (k === "x") {
        e.preventDefault();
        doAdd({ entryType: "trial", result: "correct", promptLevelId: prompt?.id ?? null });
        return;
      }
      if (k === "z") {
        e.preventDefault();
        doAdd({ entryType: "trial", result: "incorrect", promptLevelId: prompt?.id ?? null });
        return;
      }
      if (e.key === "Backspace") {
        e.preventDefault();
        doUndo();
        return;
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, readOnly, promptLevels, prompt, measurementType]);

  const doAdd = async (payload: any) => {
    if (readOnly) return;
    if (!contextValid) { showErrorMessage("Fill Student, Program, Target and Staff first."); return; }
    const sid = await requireSession();
    if (!sid) return;
    try {
      const r: any = await addEntry({ id: sid, ...payload }).unwrap();
      if (r?.status && r.data) setEntries((es) => [...es, r.data]);
      else showErrorMessage(r?.message || "Failed to add entry");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to add entry"); }
  };

  const doUndo = async () => {
    if (!sessionId || readOnly) return;
    const r: any = await undoEntry(sessionId).unwrap();
    if (r?.status && r.data) setEntries((es) => es.filter((e) => e.id !== r.data.id));
  };

  const doDelete = async (entry: any) => {
    if (!sessionId || readOnly) return;
    await deleteEntry({ id: sessionId, entryId: entry.id }).unwrap();
    setEntries((es) => es.filter((e) => e.id !== entry.id));
  };

  const trialSummary = useMemo(() => {
    // Batch 49 §3 revision — the accuracy ring is the most prominent
    // element on the page; blending trials across two targets after a
    // mid-session target change produces a clinically wrong percentage.
    // Filter to the current target's entries only. Entries from the
    // prior target keep existing in state and still render in the entry
    // log below (per their original targetId), but they do not count
    // toward the ring or the trial strip for the current target.
    const currentTargetId = target?.id ?? null;
    const t = entries.filter((e) =>
      e.entryType === "trial" &&
      (currentTargetId == null || e.targetId == null || String(e.targetId) === String(currentTargetId))
    );
    const c = t.filter((e) => e.result === "correct").length;
    const i = t.filter((e) => e.result === "incorrect").length;
    const p = t.filter((e) => e.result === "prompted").length;
    const nr = t.filter((e) => e.result === "no_response").length;
    const na = t.filter((e) => e.result === "na").length;
    const denom = c + i + p + nr;
    return {
      completed: t.length, correct: c, incorrect: i, prompted: p,
      noResponse: nr, notApplicable: na,
      percentCorrect: denom > 0 ? Math.round((c / denom) * 100) : 0,
    };
  }, [entries, target?.id]);

  const complete = async () => {
    if (!sessionId) return;
    // Batch 53 §4 — if the technician never touched End Time, stamp it
    // from the client clock now so the session has a real duration.
    // The autosave payload already carries endTime; flush it through
    // an immediate autosave BEFORE calling complete() so the backend
    // sees the value.
    if (!endTime) {
      const d = new Date();
      const stamped = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      setEndTime(stamped);
      try {
        await autosave({
          id: sessionId,
          studentId: student?.id ?? null, programId: program?.id ?? null,
          targetId: target?.id ?? null, staffId: staff?.id ?? null,
          sessionDate, measurementType, locationName: location, notes,
          startedAt: startTime && sessionDate ? `${sessionDate} ${startTime}:00` : null,
          completedAt: sessionDate ? `${sessionDate} ${stamped}:00` : null,
          promptTemplateIdSnapshot: target?.promptTemplateId ?? null,
          autosaveVersion,
        }).unwrap();
      } catch { /* fall through to complete anyway */ }
    }
    const r: any = await completeSession(sessionId).unwrap();
    if (r?.status) { showMessage("Session completed"); navigate("/aba-therapy/sessions"); }
    else showErrorMessage(r?.message || "Failed to complete");
  };

  const submit = async () => {
    if (!sessionId) return;
    const r: any = await submitReview(sessionId).unwrap();
    if (r?.status) { showMessage("Submitted for review"); navigate("/aba-therapy/sessions"); }
    else showErrorMessage(r?.message || "Failed to submit");
  };

  const taskSteps: Array<{ id: number|string; name: string }> = useMemo(() => {
    // Task-analysis steps are stored in program_targets.notes as a JSON array
    // if configured; otherwise fall back to an empty list. Editors that
    // don't use TA will simply not see any step rows.
    return [];
  }, [target]);

  const renderMode = () => {
    if (readOnly) return null;
    // Batch 15 Step 2 — explicit switch on the enum. No substring
    // matching, no order-dependence, no default-by-exclusion. An
    // unrecognised or null value renders MeasurementTypeUnsetState
    // rather than silently picking a Recorder. Renders NO recording
    // surface in that case — a therapist cannot enter data against
    // a program whose measurement type isn't set.
    const mt = toMeasurementTypeEnum(measurementType);
    switch (mt) {
      case "trial_percent_correct":
        return <TrialBasedRecorder prompt={prompt} onAdd={doAdd} onUndo={doUndo} summary={trialSummary} />;
      case "task_analysis":
        return <TaskAnalysisRecorder steps={taskSteps} entries={entries} prompt={prompt} onAdd={doAdd} />;
      case "frequency":
        return <FrequencyRecorder entries={entries} sessionStart={startTimeRef.current} onAdd={doAdd} onUndo={doUndo} />;
      case "duration":
        return <DurationRecorder entries={entries} onAdd={doAdd} />;
      case "latency":
        return <LatencyRecorder entries={entries} prompt={prompt} onAdd={doAdd} />;
      case "interval":
        return <IntervalRecorder entries={entries} onAdd={doAdd} />;
      case "abc_behaviour":
        return <AbcBehaviourRecorder entries={entries} onAdd={doAdd} onDelete={(id) => doDelete({ id })} />;
      case null:
      default:
        return <MeasurementTypeUnsetState raw={measurementType} />;
    }
  };

  // Batch 54 §2 — "When?" is now Step 4, before Staff. Time constrains
  // staff availability, so it must be captured before the staff picker.
  // effectiveStepNumber (from the autosave-rejection classifier above)
  // overrides derivation so the user lands on the offending picker with
  // its inline error, rather than deriving to step 5 (all filled) and
  // showing the wrong picker.
  const derivedStepNumber =
    !student ? 1
    : !program ? 2
    : !target ? 3
    : (!sessionDate || !startTime) ? 4
    : !staff ? 5 : 5;
  const stepNumber = effectiveStepNumber ?? derivedStepNumber;

  return (
    <Box sx={{ ...PageSurface, p: { xs: 2, md: 3 } }}>
      <Breadcrumbs sx={{ mb: 1.5, fontSize: 12 }} separator="/">
        <MLink underline="hover" component={Link} to="/aba-therapy/programs" sx={{ color: B13Ink.ink3 }}>ABA Therapy</MLink>
        <MLink underline="hover" component={Link} to="/aba-therapy/sessions" sx={{ color: B13Ink.ink3 }}>Session Data</MLink>
        <Typography sx={{ fontSize: 12, color: LegacyTokens.ink }}>{sessionId ? `Session #${sessionId}` : "Record"}</Typography>
      </Breadcrumbs>

      {/* Batch 49 §2.5 — page header matching the rest of the ABA module. */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: { md: "flex-start" }, justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", minWidth: 0 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: `${LegacyTokens.radius.control}px`,
              backgroundColor: LegacyTokens.tealSoft, color: LegacyTokens.teal,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <EventNoteIcon fontSize="medium" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography component="h1" sx={{ ...TypeScale.pageTitle, lineHeight: 1.15 }}>
                  {readOnly ? "Session (Read-only)" : sessionId ? "Continue Session" : "Record New Session"}
                </Typography>
                <Box sx={{
                  display: "inline-flex", alignItems: "center",
                  backgroundColor: LegacyTokens.tealSoft, color: LegacyTokens.teal,
                  fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  px: 1, py: 0.25, borderRadius: `${LegacyTokens.radius.chip}px`,
                }}>ABA Clinical Sessions</Box>
              </Box>
              <Typography sx={{ ...TypeScale.caption, mt: 0.5, maxWidth: 720 }}>
                {phase === "setup"
                  ? "Choose the Student, Program, Target, session time and Staff for this session."
                  : `Recording — status: ${status.replace(/_/g, " ")}`}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <AutosaveIndicator state={autosaveState} savedAt={savedAt} />
            {autosaveState === "error" && (
              <>
                <Typography variant="caption" sx={{ color: "error.main" }}>
                  {autosaveBlockingMessage ?? "Unable to save the draft."}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => {
                  setAutosaveBlockingMessage(null); setAutosaveFailures(0);
                  setAutosaveBackoffMs(1000); setManualRetryToken((n) => n + 1);
                }}>Retry Now</Button>
              </>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Page-level fallback Alert — fires when the server rejected a
         combination but the message didn't match any known field
         (staff/target/program). Rare; classifier covers the current
         message vocabulary. If we see this Alert in production, add
         the missing pattern to the classifier. */}
      {autosaveBlockingMessage && blockingStep === null && !readOnly && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 720, mx: "auto" }}>
          {autosaveBlockingMessage}
        </Alert>
      )}

      {/* ── PHASE 1 — SETUP ────────────────────────────────────────── */}
      {phase === "setup" && !readOnly && (
      <Card variant="outlined" sx={{
        p: { xs: 2, md: 3.5 }, mb: 3,
        maxWidth: 720, mx: "auto",
        borderColor: LegacyTokens.line,
        borderRadius: `${LegacyTokens.radius.card}px`,
        backgroundColor: LegacyTokens.surface,
        boxShadow: "none",
      }}>
        {/* Batch 55 §2 — answered-step summary rows. A row renders only
           for steps < stepNumber. The active step falls through and
           renders its full picker below. "Change" jumps back to that
           step by clearing everything from it onward, which lowers
           stepNumber via the existing derivation. */}
        {(() => {
          const fmtWhen = (ymd: string, hhmm: string) => {
            if (!ymd) return "";
            const [y,m,d] = ymd.split("-").map(Number);
            const dt = new Date(y, (m||1)-1, d||1);
            const dateStr = dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
            if (!hhmm) return dateStr;
            const [H,M] = hhmm.split(":").map(Number);
            const h12 = H % 12 || 12;
            const ampm = H >= 12 ? "PM" : "AM";
            return `${dateStr} · ${h12}:${String(M).padStart(2,"0")} ${ampm}`;
          };
          const rows: Array<{ label: string; value: string; onChange: () => void }> = [];
          if (stepNumber > 1 && student) rows.push({
            label: "Student", value: student.label ?? `${student.first_name ?? ""} ${student.last_name ?? ""}`.trim(),
            onChange: () => { setStudent(null); setProgram(null); setTarget(null); setStaff(null); },
          });
          if (stepNumber > 2 && program) rows.push({
            label: "Program", value: program.label ?? program.name ?? "",
            onChange: () => { setProgram(null); setTarget(null); },
          });
          if (stepNumber > 3 && target) rows.push({
            label: "Target", value: target.label ?? target.name ?? "",
            onChange: () => setTarget(null),
          });
          if (stepNumber > 4 && sessionDate && startTime) rows.push({
            label: "When", value: fmtWhen(sessionDate, startTime) + (endTime ? ` – ${(() => {
              const [H,M] = endTime.split(":").map(Number);
              const h12 = H % 12 || 12; const ampm = H >= 12 ? "PM" : "AM";
              return `${h12}:${String(M).padStart(2,"0")} ${ampm}`;
            })()}` : ""),
            onChange: () => { setSessionDate(""); setStartTime(""); setEndTime(""); },
          });
          return rows.length > 0 ? (
            <Stack spacing={0.5} sx={{ mb: 3 }}>
              {rows.map((r) => (
                <Box key={r.label} sx={{
                  display: "flex", alignItems: "center", gap: 1.5, minHeight: 40,
                  px: 1.5, borderRadius: `${LegacyTokens.radius.control}px`,
                  backgroundColor: LegacyTokens.tealSoft,
                }}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: LegacyTokens.teal, flexShrink: 0 }} />
                  <Typography sx={{ ...TypeScale.fieldLabel, color: B13Ink.ink3, minWidth: 76 }}>
                    {r.label}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: B13Ink.ink2, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.value}
                  </Typography>
                  <Button size="small" onClick={r.onChange} disabled={readOnly}
                    sx={{ color: LegacyTokens.teal, textTransform: "none", fontWeight: 500, flexShrink: 0 }}>
                    Change
                  </Button>
                </Box>
              ))}
            </Stack>
          ) : null;
        })()}

        <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 0.5 }}>
          Step {stepNumber} of 5
        </Typography>
        <Typography sx={{ ...TypeScale.sectionHead, mb: 2 }}>
          {stepNumber === 1 ? "Which Student?"
            : stepNumber === 2 ? "Which Program?"
            : stepNumber === 3 ? "Which Target?"
            : stepNumber === 4 ? "When?"
            : "Which Staff member?"}
        </Typography>

        <Stack spacing={2}>
          <Grid container spacing={2} alignItems="flex-start" data-testid="recorder-context-grid">
          {stepNumber === 1 && (
          <Grid item xs={12}>
            <AsyncPicker label="Student"
              placeholder="Select an ABA student"
              loadingText="Loading ABA students…"
              emptyText="No eligible ABA / Special Program students found."
              value={student}
              onChange={(v) => { setStudent(v); setProgram(null); setTarget(null); setStaff(null); }}
              fetch={(p) => searchStudents(p as any)}
              disabled={readOnly}
              required
              loadOnOpen
              extraQueryParams={{ groupType: studentGroup, limit: 15 }}
              groupFilter={{
                value: studentGroup,
                onChange: setStudentGroup,
                options: [
                  { value: "my_aba_students", label: "My ABA Students" },
                  { value: "all_aba",         label: "All ABA / Special Program Students" },
                ],
              }}
              renderOption={(o: any) => (
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{`${o.first_name ?? ""} ${o.last_name ?? ""}`.trim()}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {[o.reference_id,
                      o.grade_label ? `Grade ${o.grade_label}` : (o.grade_id ? `Grade ${o.grade_id}` : null),
                      o.location_id ? `Loc ${o.location_id}` : null].filter(Boolean).join(" · ")}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    {o.aba && <Chip size="small" color="primary" variant="outlined" label="ABA / Special Program" />}
                    {o.assigned_staff && <Chip size="small" variant="outlined" label={`Staff: ${o.assigned_staff}`} />}
                    <Chip size="small" variant="outlined" label={`${o.active_programs_count ?? 0} active Program${o.active_programs_count === 1 ? "" : "s"}`} />
                  </Stack>
                </Box>
              )}
            />
          </Grid>
          )}
          {stepNumber === 2 && (
          <Grid item xs={12}>
            {blockingStep === 2 && autosaveBlockingMessage && (
              <Alert severity="error" sx={{ mb: 1.5 }}>{autosaveBlockingMessage}</Alert>
            )}
            <AsyncPicker label="Program"
              placeholder={student ? "Select a Program assigned to this Student" : "Select a Student to see assigned Programs"}
              // Batch 53 §3 — honest empty-state copy. The picker says
              // "No matches" by default which reads as a search failure;
              // the truth is that this Student has no ABA Program
              // assigned, which is a setup step, not a search result.
              emptyText={student ? "This Student has no assigned ABA Program. Use Assign ABA Program below to add one." : "Select a Student first."}
              value={program} onChange={(v) => { setProgram(v); setTarget(null); }}
              fetch={(p) => searchPrograms(p as any)} disabled={readOnly || !student}
              extraQueryParams={student ? { studentId: student.id } : undefined}
              required loadOnOpen={!!student} minChars={0} />
            {student && student.active_programs_count === 0 && (
              <Alert severity="warning"
                sx={{ mt: 0.75, py: 0.5, "& .MuiAlert-action": { alignItems: "center", pt: 0 } }}
                action={
                  <Button size="small" variant="contained" color="warning"
                    onClick={() => setAssignDrawerOpen(true)}
                    sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                    Assign ABA Program
                  </Button>
                }>
                No assigned ABA Program — this Student needs an active ABA Program before Session Data can be recorded.
              </Alert>
            )}
          </Grid>
          )}
          {stepNumber === 3 && (
          <Grid item xs={12}>
            {blockingStep === 3 && autosaveBlockingMessage && (
              <Alert severity="error" sx={{ mb: 1.5 }}>{autosaveBlockingMessage}</Alert>
            )}
            <AsyncPicker label="Target"
              placeholder={program ? "Select an active Target from this Program" : "Select a Program to see active Targets"}
              value={target} onChange={setTarget}
              fetch={(p) => searchTargets(p as any)} disabled={readOnly || !program}
              extraQueryParams={program ? { programId: program.id } : undefined}
              required loadOnOpen={!!program} minChars={0} />
          </Grid>
          )}
          {stepNumber === 4 && (<>
          {/* Batch 55 §1 — swap native date/time inputs for the premium
             pickers (AppDatePicker / PremiumTimePicker) that the app
             already uses elsewhere. Batch 55 §3 — all three helpers are
             one line each so the row bottoms align. */}
          <Grid item xs={12} sm={4}>
            <AppDatePicker label="Session Date" required size="small" fullWidth
              value={sessionDate} onChange={setSessionDate}
              disabled={readOnly} helperText="Defaults to today." />
          </Grid>
          <Grid item xs={6} sm={4}>
            <PremiumTimePicker label="Start Time" size="small" fullWidth
              value={startTime} onChange={setStartTime}
              disabled={readOnly}
              helperText="Defaults to when you opened the recorder." />
          </Grid>
          <Grid item xs={6} sm={4}>
            <PremiumTimePicker label="End Time" size="small" fullWidth
              value={endTime} onChange={setEndTime} clearable
              disabled={readOnly}
              helperText="Stamped on Complete if blank." />
          </Grid>
          </>)}
          {stepNumber === 5 && (
          <Grid item xs={12}>
            {blockingStep === 5 && autosaveBlockingMessage && (
              <Alert severity="error" sx={{ mb: 1.5 }}>{autosaveBlockingMessage}</Alert>
            )}
            <AsyncPicker label="Staff"
              placeholder={
                !student ? "Select a Student first"
                : !program ? "Select a Program first"
                : (!sessionDate || !startTime) ? "Set the session date and start time first"
                : "Select ABA Staff"
              }
              loadingText="Loading ABA Staff…"
              // Batch 53 §3 — was "for this Student's location". Location
              // rejected 0 of 90 staff in the live diagnostic; the actual
              // gate is ABA-eligibility (role match, active psa row,
              // Special Program class, or ABA permission). Say that.
              // The old copy sent operators chasing a location problem
              // that didn't exist.
              emptyText="No ABA-qualified staff match. Subject assignments — including 'ABA Therapy' — do not make someone ABA staff. Give a staff member the ABA Staff role in User Management, or assign them to this Student first."
              value={staff} onChange={setStaff}
              // Client-side hard-filter for the recorder's policy —
              // the assign endpoint returns location-mismatched and
              // PSA-mismatched rows as annotations (soft policy: admin
              // can assign across locations, or preview who's on the
              // program's assignments). The recorder needs the hard
              // policy: unauthorised staff never appear.
              //
              // Two flags close the two remaining gates:
              //   - location_mismatch === true → wrong location
              //   - psa_match === false → program has PSAs but this
              //     staff isn't on one. Tri-state: null means "no
              //     PSA-gate applies" (program has no assignments yet,
              //     or program not supplied), which reads as pass via
              //     `!== false`.
              //
              // The other four checks (deleted / no user / inactive /
              // suspended) are already hard-filtered by the assign
              // endpoint. Six of six closed at the picker; the
              // autosave `validateChain` remains as backstop for
              // genuine concurrent modification (staff suspended
              // between fetch and submit).
              fetch={async (p) => {
                const r: any = await searchAbaStaff(p as any);
                const arr = Array.isArray(r?.data?.data) ? r.data.data : [];
                return { data: { data: filterEligibleForRecorder(arr) } };
              }}
              // Gate on program in addition to student — passing
              // program_id to the endpoint lets the enrichment score
              // PSA relationships properly, and the picker should
              // never fire before step 2 (program) completes.
              disabled={readOnly || !student || !program || !sessionDate || !startTime}
              required
              loadOnOpen={!!student && !!program && !!sessionDate && !!startTime}
              minChars={0}
              extraQueryParams={student && program ? {
                student_id: student.id,
                program_id: program.id,
                group_type: "location",
                limit: 15,
              } : undefined}
              renderOption={(o: any) => {
                const availLabel = ({
                  available: "Available",
                  near_capacity: "Near Capacity",
                  at_capacity: "At Capacity",
                } as Record<string, string>)[o.availability_status] ?? o.availability_status;
                const relationship = ({
                  assigned_to_student_and_program: "Assigned to this Student and Program",
                  assigned_to_student: "Assigned to this Student",
                  assigned_to_program: "Assigned to this Program",
                  same_location: "Same Location",
                  location_unknown: "Location not set",
                  authorized_elsewhere: "Authorized at another location",
                } as Record<string, string>)[o.assignment_relationship] ?? "Eligible";
                return (
                  <Box sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>{o.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {[o.role || "ABA Staff", (o.locations && o.locations.length) ? `Loc ${o.locations.join(", ")}` : "Location not set"].filter(Boolean).join(" · ")}
                    </Typography>
                    {o.aba_qualification_source && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic" }}>
                        {o.aba_qualification_source}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" variant="outlined" label={relationship} />
                      {o.multi_location && <Chip size="small" variant="outlined" label="Multi-location" />}
                      <Chip size="small" variant="outlined" label={`Caseload: ${o.caseload_count}`} />
                      <Chip size="small" color={
                        o.availability_status === "at_capacity" ? "error"
                        : o.availability_status === "near_capacity" ? "warning"
                        : "success"
                      } label={availLabel} />
                      {o.is_suspended && <Chip size="small" color="error" label="Suspended" />}
                      {o.invitation_pending && <Chip size="small" color="warning" label="Invitation pending" />}
                    </Stack>
                  </Box>
                );
              }}
            />
            {autoFillWarning && (
              <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                {autoFillWarning}
              </Typography>
            )}
          </Grid>
          )}
        </Grid>

        {/* Batch 49 §2.2 — More options behind a disclosure.
           Batch 54 §3 — Date/Start/End moved out to Step 4. Only
           Measurement (auto-derived from target), Location and Notes
           remain. Disclosure kept: none of the three needs attention
           on the golden path (Measurement auto-fills, Location and
           Notes are optional), so hiding them keeps the setup form
           tight. Inlining would add visual weight for fields the
           technician rarely touches.
           Batch 55 §4 (revised) — only render on Step 5. On earlier
           steps the card must ask one question and only one. */}
        {stepNumber === 5 && (
        <Box>
          <Button
            size="small"
            onClick={() => setMoreOptionsOpen((v) => !v)}
            startIcon={moreOptionsOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            sx={{ color: B13Ink.ink3, textTransform: "none", fontWeight: 500, px: 0 }}
          >
            {moreOptionsOpen ? "Hide options" : "More options (Measurement · Location · Notes)"}
          </Button>
          <Collapse in={moreOptionsOpen} unmountOnExit>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField select size="small" fullWidth label="Measurement Type"
                  value={measurementType}
                  onChange={(e) => setMeasurementType(e.target.value)} disabled={readOnly}
                  InputLabelProps={{ shrink: true }}>
                  {[
                    { value: "Percent Correct", label: "Trial Accuracy" },
                    { value: "Task Analysis",   label: "Task Analysis" },
                    { value: "Frequency",       label: "Frequency" },
                    { value: "Duration",        label: "Duration" },
                    { value: "Latency",         label: "Latency" },
                    { value: "Interval",        label: "Interval Recording" },
                    { value: "ABC Behaviour",   label: "ABC Behaviour" },
                  ].map(({ value, label }) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Location" value={location}
                  onChange={(e) => setLocation(e.target.value)} disabled={readOnly}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth multiline minRows={2} label="Notes"
                  value={notes} onChange={(e) => setNotes(e.target.value)} disabled={readOnly}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </Collapse>
        </Box>
        )}
        </Stack>

        {/* Setup footer — only Cancel is meaningful before Phase 2. */}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button onClick={() => navigate("/aba-therapy/sessions")}
            sx={{ color: B13Ink.ink3, textTransform: "none" }}>Cancel</Button>
        </Stack>
      </Card>
      )}

      {/* Batch 51 §2's full-page "Cannot record this session" block
         card was removed here. Per-field autosave rejections now surface
         as inline Alerts at the offending wizard step (see the
         blockingStep classifier above and the Alerts inside each
         picker's stepNumber block). The rejection no longer takes over
         the page — the wizard stays intact and other selections are
         preserved. */}

      {/* ── PHASE 2 — RECORD ───────────────────────────────────────── */}
      {phase === "record" && (
        <>
          <ContextStrip
            student={student} program={program} target={target} staff={staff}
            sessionDate={sessionDate} measurementType={measurementType}
            onEdit={() => setContextEditOpen(true)}
          />

          {/* Branch on the enum, not the varchar label — was
              `measurementType === "Percent Correct"`, which sent
              enum-shape sessions ("trial_percent_correct") into the
              non-trial recorder branch below and rendered the wrong
              surface. Same fourth-instance defect as the label
              ternaries in SessionData/List. */}
          {toMeasurementTypeEnum(measurementType) === "trial_percent_correct" ? (
            <RecordingSurface
              levels={promptLevels}
              currentPrompt={prompt}
              onPromptChange={setPrompt}
              entries={entries}
              currentTargetId={target?.id ?? null}
              readOnly={readOnly}
              prefersReducedMotion={prefersReducedMotion}
              onCorrect={() => doAdd({ entryType: "trial", result: "correct", promptLevelId: prompt?.id ?? null })}
              onIncorrect={() => doAdd({ entryType: "trial", result: "incorrect", promptLevelId: prompt?.id ?? null })}
              onPrompted={() => doAdd({ entryType: "trial", result: "prompted", promptLevelId: prompt?.id ?? null })}
              onNoResponse={() => doAdd({ entryType: "trial", result: "no_response", promptLevelId: prompt?.id ?? null })}
              onNa={() => doAdd({ entryType: "trial", result: "na", promptLevelId: prompt?.id ?? null })}
              onUndo={doUndo}
              trialSummary={trialSummary}
              promptTemplateLoading={!!effectivePromptTemplateId && promptTemplateLoading}
            />
          ) : (
            // Other measurement types keep their existing recorder
            // components — they weren't the point of Batch 49 §2.3.
            <Card variant="outlined" sx={{
              p: 2.5, mb: 2, borderColor: LegacyTokens.line, borderRadius: `${LegacyTokens.radius.card}px`,
              backgroundColor: LegacyTokens.surface, boxShadow: "none",
            }}>
              <Typography sx={{ ...TypeScale.sectionHead, mb: 1.5 }}>
                {/* Enum→label — was raw {measurementType} which
                    rendered "trial_percent_correct" instead of
                    "Trial Accuracy" whenever the session row stored
                    the enum in that field. */}
                {measurementTypeLabel(toMeasurementTypeEnum(measurementType) as any) || measurementType}
              </Typography>
              {renderMode()}
            </Card>
          )}

          {/* Entry log — kept, moved below the fold per §2.3(f). */}
          <Card variant="outlined" sx={{
            p: 2, mt: 3,
            borderColor: LegacyTokens.line, borderRadius: `${LegacyTokens.radius.card}px`,
            backgroundColor: LegacyTokens.surface, boxShadow: "none",
          }}>
            <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 1.5 }}>
              Entry Log ({entries.length})
            </Typography>
            <SessionEntryLog entries={entries as any} readOnly={readOnly} onDelete={doDelete} />
          </Card>

          {!readOnly && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end"
              sx={{ mt: 3, pr: { xs: 0, md: "80px" }, pb: { xs: "80px", md: 1 } }}>
              <Button onClick={() => navigate("/aba-therapy/sessions")}
                sx={{ color: B13Ink.ink3, textTransform: "none" }}>Cancel</Button>
              <Button variant="outlined" startIcon={<SaveIcon />}
                onClick={() => showMessage("Draft saved (autosave)")}
                sx={{ textTransform: "none" }}>Save Draft</Button>
              <Button variant="contained" color="success" startIcon={<DoneAllIcon />}
                onClick={complete} disabled={!sessionId || entries.length === 0}
                sx={{ textTransform: "none" }}>Complete Session</Button>
              <Button variant="contained" startIcon={<RateReviewIcon />}
                onClick={submit} disabled={!sessionId || entries.length === 0}
                sx={{ textTransform: "none" }}>Submit for Review</Button>
            </Stack>
          )}

          {/* Compact context-edit dialog — reached from the "Edit" chip
             on the context strip. Reuses the setup pickers so changing
             the target mid-session goes through the same paths as
             during setup, keeping the autosave contract intact. */}
          <Dialog open={contextEditOpen} onClose={() => setContextEditOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit session context</DialogTitle>
            <DialogContent dividers>
              <Alert severity="info" sx={{ mb: 2 }}>
                Trials already recorded stay attached to their original Target in the entry log.
              </Alert>
              <Stack spacing={2}>
                <AsyncPicker label="Target" value={target} onChange={setTarget}
                  fetch={(p) => searchTargets(p as any)}
                  extraQueryParams={program ? { programId: program.id } : undefined}
                  loadOnOpen={!!program} minChars={0} />
                <AppDatePicker label="Session Date" size="small" fullWidth
                  value={sessionDate} onChange={setSessionDate} />
                <TextField size="small" fullWidth label="Location"
                  value={location} onChange={(e) => setLocation(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
                <TextField size="small" fullWidth multiline minRows={2} label="Notes"
                  value={notes} onChange={(e) => setNotes(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setContextEditOpen(false)}>Done</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Draft recovery */}
      <Dialog open={recoverOpen} onClose={() => setRecoverOpen(false)}>
        <DialogTitle>Unfinished Session Found</DialogTitle>
        <DialogContent>
          An unfinished Session already exists for this Student and Target
          {recoverCandidate?.lastAutosavedAt ? ` (last saved ${new Date(recoverCandidate.lastAutosavedAt).toLocaleString()})` : ""}.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRecoverOpen(false); navigate(`/aba-therapy/sessions/${recoverCandidate.id}/edit`); }}>Continue Draft</Button>
          <Button onClick={() => setRecoverOpen(false)} color="inherit">Start Separate Session</Button>
        </DialogActions>
      </Dialog>

      {student && (
        <AssignAbaProgramDrawer
          open={assignDrawerOpen}
          student={student}
          defaultStaff={staff}
          defaultDate={sessionDate}
          onClose={() => setAssignDrawerOpen(false)}
          onAssigned={({ programId, programName, staffId, staffName }) => {
            // Mark the Student as now having a Program (so the warning card hides)
            // and preselect the new Program so the Recorder can chain forward.
            setStudent((s: any) => s ? { ...s, active_programs_count: (s.active_programs_count ?? 0) + 1 } : s);
            setProgram({ id: programId, label: programName || `Program #${programId}` });
            if (staffId && !staff) setStaff({ id: staffId, label: staffName || `Staff #${staffId}` });
          }}
        />
      )}
    </Box>
  );
}

// ─── Batch 49 §2.2 — compact context strip for Phase 2 ─────────────────
// Batch 15 Step 2 — rendered when the effective measurement type is
// null or an unrecognised value. Written for a therapist mid-session,
// not for a developer. Explicit dead end: no Recorder mounts, no
// input surface a therapist could type into. The alternative
// (silently picking a Recorder) is the defect we are fixing.
const MeasurementTypeUnsetState: React.FC<{ raw: string | null | undefined }> = ({ raw }) => {
  const shown = raw && String(raw).trim() !== "" ? String(raw).trim() : null;
  return (
    <Box sx={{
      p: 3, textAlign: "center",
      border: `1px dashed ${LegacyTokens.line}`,
      borderRadius: `${LegacyTokens.radius.card}px`,
      backgroundColor: LegacyTokens.surface,
    }} data-testid="measurement-type-unset">
      <Typography sx={{ ...TypeScale.sectionHead, mb: 1 }}>
        Measurement type isn't set for this program.
      </Typography>
      <Typography sx={{ ...TypeScale.caption, maxWidth: 520, mx: "auto" }}>
        There's no data recorder for it, so this session can't be started here.
        Open the program's Treatment tab and choose a measurement type first.
      </Typography>
      {shown && (
        <Typography sx={{ ...TypeScale.caption, mt: 1.5, fontStyle: "italic" }}>
          Current value: “{shown}”
        </Typography>
      )}
    </Box>
  );
};

const ContextStrip: React.FC<{
  student: any; program: any; target: any; staff: any;
  sessionDate: string; measurementType: string;
  onEdit: () => void;
}> = ({ student, program, target, staff, sessionDate, measurementType, onEdit }) => (
  <Card variant="outlined" sx={{
    p: 1.5, mb: 2,
    borderColor: LegacyTokens.line, borderRadius: `${LegacyTokens.radius.card}px`,
    backgroundColor: LegacyTokens.surface, boxShadow: "none",
  }}>
    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
        <StudentAvatar name={student?.label} size={40} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: LegacyTokens.ink }} noWrap>
            {student?.label}
          </Typography>
          <Typography sx={{ fontSize: 12, color: B13Ink.ink3 }} noWrap>
            {/* Route through the enum→label helper so any measurement
                type displays as "Trial Accuracy" / "Task Analysis" /
                etc. — was a one-case hand-rolled ternary that showed
                the raw enum for every other type. */}
            {sessionDate} · {measurementTypeLabel(toMeasurementTypeEnum(measurementType) as any) || measurementType}
          </Typography>
        </Box>
      </Stack>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", minWidth: 0, flexGrow: 1 }}>
        <StripCell label="Program" value={program?.label} />
        <StripCell label="Target" value={target?.label} />
        <StripCell label="Staff" value={staff?.label} />
      </Box>
      <Button
        size="small" startIcon={<EditIcon fontSize="small" />}
        onClick={onEdit}
        sx={{
          color: B13.primary, textTransform: "none", fontWeight: 600,
          border: `1px solid ${tint12(B13.primary)}`,
          "&:hover": { backgroundColor: tint12(B13.primary) },
          flexShrink: 0,
        }}
      >Edit</Button>
    </Stack>
  </Card>
);

const StripCell: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ ...TypeScale.fieldLabel, display: "block", fontSize: 10 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: B13Ink.ink2 }} noWrap>
      {value ?? "—"}
    </Typography>
  </Box>
);

// ─── Batch 49 §2.3 — the recording surface ─────────────────────────────
//
// The signature of this page. Big accuracy ring, two large outcome
// buttons, prompt-level chips in the target's hierarchy colours, and a
// trial strip showing the whole session at a glance. Undo is always
// visible. Every interactive element ≥ 44px, outcome buttons ≥ 64px so
// the page is usable one-handed on an iPad next to a child.
//
const RecordingSurface: React.FC<{
  levels: Array<{ id: number; levelName: string; colour: string; shortCode?: string | null }>;
  currentPrompt: any;
  onPromptChange: (v: any) => void;
  entries: any[];
  currentTargetId: number | string | null;
  readOnly: boolean;
  prefersReducedMotion: boolean;
  onCorrect: () => void;
  onIncorrect: () => void;
  // T63 Part N — the three secondary trial results that the Batch 49
  // redesign silently dropped. Backend + aggregator have always
  // accepted them; adding the writers here restores the full canonical
  // TrialResult contract so Not Counted / Prompted / No Response can
  // be recorded via UI without going through the API directly.
  onPrompted: () => void;
  onNoResponse: () => void;
  onNa: () => void;
  onUndo: () => void;
  trialSummary: { completed: number; correct: number; percentCorrect: number };
  promptTemplateLoading: boolean;
}> = ({
  levels, currentPrompt, onPromptChange, entries, currentTargetId,
  readOnly, prefersReducedMotion,
  onCorrect, onIncorrect, onPrompted, onNoResponse, onNa,
  onUndo, trialSummary, promptTemplateLoading,
}) => {
  // Batch 49 §3 revision — trial strip filters to the current target so
  // it matches what the ring shows. A mid-session target change means
  // the strip resets alongside the ring; prior trials remain visible in
  // the entry log below, attributed to their original target.
  const trialEntries = entries.filter((e) =>
    e.entryType === "trial" &&
    (currentTargetId == null || e.targetId == null || String(e.targetId) === String(currentTargetId))
  );
  const canUndo = trialEntries.length > 0 && !readOnly;
  return (
    <Card variant="outlined" sx={{
      p: { xs: 2, md: 3 },
      borderColor: LegacyTokens.line, borderRadius: `${LegacyTokens.radius.card}px`,
      backgroundColor: LegacyTokens.surface, boxShadow: "none",
    }}>
      <Grid container spacing={3} alignItems="center">
        {/* (a) — Accuracy ring, centre-left. */}
        <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: "center" }}>
          <AccuracyRing
            percent={trialSummary.percentCorrect}
            count={trialSummary.completed}
            correct={trialSummary.correct}
            reducedMotion={prefersReducedMotion}
          />
        </Grid>

        {/* (b) — Outcome buttons, side by side, ≥ 64px tall. */}
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  fullWidth variant="contained" onClick={onCorrect} disabled={readOnly}
                  aria-label="Record correct trial (X)"
                  sx={{
                    height: 72, fontSize: 18, fontWeight: 700, textTransform: "none",
                    backgroundColor: LegacyTokens.emerald, color: LegacyTokens.surface,
                    borderRadius: `${LegacyTokens.radius.control}px`,
                    "&:hover": { backgroundColor: LegacyTokens.emerald, filter: "brightness(0.95)" },
                    "&.Mui-disabled": { backgroundColor: B13.canvas, color: B13Ink.ink3 },
                  }}
                >
                  Correct
                  <Box component="span" sx={{
                    ml: 1, px: 0.75, py: 0.15, borderRadius: `${LegacyTokens.radius.chip}px`,
                    backgroundColor: "rgba(255,255,255,0.2)", fontSize: 11, fontWeight: 700,
                  }}>X</Box>
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth variant="contained" onClick={onIncorrect} disabled={readOnly}
                  aria-label="Record incorrect trial (Z)"
                  sx={{
                    height: 72, fontSize: 18, fontWeight: 700, textTransform: "none",
                    backgroundColor: LegacyTokens.red, color: LegacyTokens.surface,
                    borderRadius: `${LegacyTokens.radius.control}px`,
                    "&:hover": { backgroundColor: LegacyTokens.red, filter: "brightness(0.95)" },
                    "&.Mui-disabled": { backgroundColor: B13.canvas, color: B13Ink.ink3 },
                  }}
                >
                  Incorrect
                  <Box component="span" sx={{
                    ml: 1, px: 0.75, py: 0.15, borderRadius: `${LegacyTokens.radius.chip}px`,
                    backgroundColor: "rgba(255,255,255,0.2)", fontSize: 11, fontWeight: 700,
                  }}>Z</Box>
                </Button>
              </Grid>
            </Grid>

            {/* T63 Part N — secondary result actions. Common actions
                stay on the primary Correct/Incorrect row; Prompted /
                No Response / N/A get a compact secondary row so all
                five canonical TrialResult values are recordable
                without hunting through a menu. */}
            <Grid container spacing={1.25}>
              <Grid item xs={4}>
                <Button
                  fullWidth variant="outlined" onClick={onPrompted} disabled={readOnly}
                  aria-label="Record prompted trial"
                  sx={{
                    height: 44, fontSize: 13, fontWeight: 600, textTransform: "none",
                    borderColor: "#7EA3C3", color: "#274F70", bgcolor: "#F1FAFF",
                    borderRadius: `${LegacyTokens.radius.control}px`,
                    "&:hover": { bgcolor: "#DEF1FB", borderColor: "#7EA3C3" },
                    "&.Mui-disabled": { bgcolor: B13.canvas, color: B13Ink.ink3 },
                  }}
                >
                  Prompted
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  fullWidth variant="outlined" onClick={onNoResponse} disabled={readOnly}
                  aria-label="Record no response trial"
                  sx={{
                    height: 44, fontSize: 13, fontWeight: 600, textTransform: "none",
                    borderColor: "#F2D05C", color: "#8A6A08", bgcolor: "#FFFDF2",
                    borderRadius: `${LegacyTokens.radius.control}px`,
                    "&:hover": { bgcolor: "#FFF8D8", borderColor: "#F2D05C" },
                    "&.Mui-disabled": { bgcolor: B13.canvas, color: B13Ink.ink3 },
                  }}
                >
                  No Response
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Tooltip title="Excluded from scoring" arrow>
                  <Button
                    fullWidth variant="outlined" onClick={onNa} disabled={readOnly}
                    aria-label="Record N/A trial"
                    sx={{
                      height: 44, fontSize: 13, fontWeight: 600, textTransform: "none",
                      borderColor: "#D9E3EC", color: "#58728C", bgcolor: "#F5F8FB",
                      borderRadius: `${LegacyTokens.radius.control}px`,
                      "&:hover": { bgcolor: "#E4ECF5", borderColor: "#7EA3C3" },
                      "&.Mui-disabled": { bgcolor: B13.canvas, color: B13Ink.ink3 },
                    }}
                  >
                    N/A
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>

            {/* (c) — Prompt chips in the hierarchy's colours. */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography sx={{ ...TypeScale.fieldLabel }}>Prompt Level</Typography>
                <Tooltip title="Undo the last trial (Backspace)" arrow>
                  <span>
                    <IconButton
                      onClick={onUndo} disabled={!canUndo}
                      aria-label="Undo last trial"
                      sx={{
                        color: B13Ink.ink3, width: 40, height: 40,
                        "&:hover": { backgroundColor: B13.canvas, color: B13Ink.ink2 },
                      }}
                    ><UndoIcon fontSize="small" /></IconButton>
                  </span>
                </Tooltip>
              </Stack>
              {promptTemplateLoading ? (
                <Box sx={{ py: 1 }}><CircularProgress size={18} sx={{ color: B13.primary }} /></Box>
              ) : levels.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: B13Ink.ink3 }}>
                  No prompt level template assigned to this target — trials will record without a prompt level.
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {levels.map((lvl, i) => {
                    const active = currentPrompt?.id === lvl.id;
                    const colour = lvl.colour || LegacyTokens.blue;
                    return (
                      <Button
                        key={lvl.id}
                        onClick={() => onPromptChange(lvl)}
                        disabled={readOnly}
                        aria-pressed={active}
                        aria-label={`Prompt level ${i + 1}: ${lvl.levelName} (press ${i + 1})`}
                        sx={{
                          minHeight: 44, px: 1.5, py: 0.5,
                          textTransform: "none", fontWeight: 600, fontSize: 13,
                          borderRadius: `${LegacyTokens.radius.chip}px`,
                          border: `1px solid ${colour}`,
                          backgroundColor: active ? colour : "transparent",
                          color: active ? LegacyTokens.surface : colour,
                          "&:hover": {
                            backgroundColor: active ? colour : `${colour}22`,
                          },
                        }}
                      >
                        <Box component="span" sx={{
                          display: "inline-block", width: 18, height: 18,
                          borderRadius: `${LegacyTokens.radius.chip}px`,
                          backgroundColor: active ? "transparent" : colour,
                          border: active ? `1px solid ${LegacyTokens.surface}` : "none",
                          color: active ? LegacyTokens.surface : LegacyTokens.surface,
                          fontSize: 10, fontWeight: 700, lineHeight: "18px",
                          textAlign: "center", mr: 0.75, opacity: active ? 0 : 1,
                        }}>{i + 1}</Box>
                        {lvl.shortCode || lvl.levelName}
                      </Button>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Stack>
        </Grid>

        {/* (d) — Trial strip below, one square per trial. */}
        <Grid item xs={12}>
          <Divider sx={{ mb: 2, borderColor: LegacyTokens.line }} />
          <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 1 }}>
            Trials this session ({trialEntries.length})
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {trialEntries.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: B13Ink.ink3 }}>
                No trials yet. Press Correct or Incorrect to record the first one.
              </Typography>
            ) : (
              trialEntries.map((t, i) => {
                const good = t.result === "correct";
                const bg = good ? LegacyTokens.emerald : LegacyTokens.red;
                const code = (t.promptLevelShortCode ?? "").toString().slice(0, 3);
                return (
                  <Box key={t.id ?? i} sx={{
                    width: 28, height: 28,
                    borderRadius: `${LegacyTokens.radius.chip}px`,
                    backgroundColor: bg,
                    color: LegacyTokens.surface,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700,
                  }}>{code}</Box>
                );
              })
            )}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
};

// ─── Batch 49 §2.3(a) — the accuracy ring ──────────────────────────────
const AccuracyRing: React.FC<{
  percent: number; count: number; correct: number; reducedMotion: boolean;
}> = ({ percent, count, correct, reducedMotion }) => {
  const size = 180;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent || 0));
  const dash = (clamped / 100) * circ;
  // Batch 51 §4a — at 0 trials the ring must be clean. Previously the
  // emerald arc was still rendered with dasharray "0 circ" and a round
  // cap, which SVG renders as a small dot at the start of the arc
  // (12 o'clock). Suppress the arc entirely at 0 trials and drop the
  // em-dash in the middle; the "0 of 0 trials" caption is enough.
  const showArc = count > 0;
  return (
    <Box
      role="figure"
      aria-label={count === 0 ? "No trials recorded yet" : `${clamped} percent correct, ${correct} of ${count} trials`}
      sx={{ position: "relative", width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={B13.canvas} strokeWidth={stroke}
        />
        {showArc && (
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none" stroke={LegacyTokens.emerald} strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ}`}
            strokeDashoffset={0}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: reducedMotion ? "none" : "stroke-dasharray 200ms ease-out" }}
          />
        )}
      </svg>
      <Box sx={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
      }}>
        {count > 0 && (
          <Typography sx={{ fontSize: 44, fontWeight: 700, color: LegacyTokens.ink, lineHeight: 1 }}>
            {`${Math.round(clamped)}%`}
          </Typography>
        )}
        <Typography sx={{ fontSize: 12, color: B13Ink.ink3, mt: count > 0 ? 0.5 : 0 }}>
          {correct} of {count} trials
        </Typography>
      </Box>
    </Box>
  );
};
